import { create } from 'zustand';
import type { IsoDate, Species } from './api/contract';

export type SheetHeight = 'replie' | 'moitie' | 'plein';
/** Carte (accueil), écran « avant de partir », sortie en cours. */
export type Mode = 'carte' | 'avant-sortie' | 'sortie';
/** Page ouverte dans le panneau à la place de la frise ou de la fiche. */
export type Panel = 'menu' | 'methode' | 'sorties' | 'regles' | null;

export const ALL_SPECIES: Species[] = ['cepe', 'girolle'];

interface AppState {
  /** Espèces affichées : les deux par défaut, l'utilisateur filtre. Jamais vide. */
  shown: Species[];
  /** Espèce dont la fiche détaille les raisons. */
  focus: Species;
  /** Jour affiché sur la carte ; null tant que la date du serveur n'est pas connue. */
  date: IsoDate | null;
  /** Maille dont la fiche est ouverte (résolution 8, 9 ou 10). */
  selected: string | null;
  /** Emprise visible de la carte [ouest, sud, est, nord] et zoom. */
  bbox: [number, number, number, number] | null;
  zoom: number;
  /** Hauteur du panneau du bas sur mobile. */
  sheet: SheetHeight;
  mode: Mode;
  panel: Panel;
  openPanel: (panel: Panel) => void;
  /** Incrémenté pour demander à la carte de revenir sur la zone couverte. */
  homeRequest: number;
  goHome: () => void;
  /** Sortie en cours (identifiant Dexie). */
  outingId: string | null;
  /** Maille d'où l'on a demandé à démarrer la sortie. */
  outingTarget: string | null;
  toggleSpecies: (species: Species) => void;
  setFocus: (species: Species) => void;
  prepareOuting: (target: string | null) => void;
  enterOuting: (id: string) => void;
  leaveOuting: () => void;
  setView: (bbox: [number, number, number, number], zoom: number) => void;
  setSheet: (sheet: SheetHeight) => void;
  setDate: (date: IsoDate) => void;
  select: (h3: string | null) => void;
  /** Reprend le filtre (et la maille d'un lien direct) après l'hydratation de la coque prérendue. */
  restore: () => void;
}

const save = (shown: Species[]) => {
  try {
    localStorage.setItem('pousse:especes', shown.join(','));
  } catch {
    // Stockage indisponible (navigation privée) : le choix vaut pour la session.
  }
};

const parseShown = (value: string | null): Species[] | null => {
  const list = (value ?? '').split(',').filter((s): s is Species => s === 'cepe' || s === 'girolle');
  return list.length ? ALL_SPECIES.filter((s) => list.includes(s)) : null;
};

export const useApp = create<AppState>((set, get) => ({
  shown: ALL_SPECIES,
  focus: 'cepe',
  date: null,
  selected: null,
  bbox: null,
  zoom: 9,
  sheet: 'replie',
  mode: 'carte',
  panel: null,
  openPanel: (panel) => set({ panel, selected: null, sheet: panel ? 'plein' : 'replie' }),
  homeRequest: 0,
  goHome: () => set((s) => ({ homeRequest: s.homeRequest + 1 })),
  outingId: null,
  outingTarget: null,
  toggleSpecies: (species) => {
    const { shown, focus } = get();
    const next = shown.includes(species) ? shown.filter((s) => s !== species) : ALL_SPECIES.filter((s) => s === species || shown.includes(s));
    if (next.length === 0) return; // au moins une espèce reste affichée
    save(next);
    set({ shown: next, focus: next.includes(focus) ? focus : next[0]! });
  },
  setFocus: (focus) => set({ focus }),
  prepareOuting: (outingTarget) => set({ mode: 'avant-sortie', outingTarget }),
  enterOuting: (outingId) => set({ mode: 'sortie', outingId, selected: null }),
  leaveOuting: () => set({ mode: 'carte', outingId: null, outingTarget: null, sheet: 'replie' }),
  setDate: (date) => set({ date }),
  // Ouvrir une fiche monte le panneau à mi-hauteur ; la fermer le replie sur la frise.
  select: (selected) => set({ selected, panel: null, sheet: selected ? 'moitie' : 'replie' }),
  setView: (bbox, zoom) => set({ bbox, zoom }),
  setSheet: (sheet) => set({ sheet }),
  restore: () => {
    // Lien direct (?especes=girolle&maille=…) d'abord, puis le dernier choix enregistré.
    const params = new URLSearchParams(location.search);
    const maille = params.get('maille');
    if (maille && /^[0-9a-f]{15}$/.test(maille)) set({ selected: maille, sheet: 'moitie' });
    let shown = parseShown(params.get('especes') ?? params.get('espece'));
    if (!shown) {
      try {
        shown = parseShown(localStorage.getItem('pousse:especes'));
      } catch {
        shown = null;
      }
    }
    if (shown) set({ shown, focus: shown[0]! });
  },
}));
